import type { TableProps } from "antd";
type FilterValue = Parameters<NonNullable<TableProps['onChange']>>[1][string];
import React, {useEffect, useState} from "react";
import {useLocation, useHistory} from "react-router-dom";
import useGetGeneralSettings from "@src/providers/useGetGeneralSettings";

export const tableOnChangeGenerator = (setSort: Function, page: number, setPage: Function) => {
    const handleTableChange = (pagination: any, filters: Record<string, FilterValue | null>, sorter: any, action:any) => {
        if (action.action === "sort") {
            const sorterKey = sorter.column?.sorter?.key || sorter.columnKey
            setSort(sorterKey)

        } else if (action.action === "paginate") {
            setPage(pagination.current)
        }
    }
    return handleTableChange
}


export const onExpandIcon = (toggleRowExpansion: Function, onExpand: Function|undefined) => {
    const onExpandByIcon = (record: any, expanded: boolean, e:any) => {
        toggleRowExpansion(record.name);
        if (expanded && onExpand)
            onExpand(expanded, record);
    }

    return (props: any) => {
        const { expanded, onExpand, record } = props
        return (
            expanded ? (
                <button
                    type="button"
                    className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded"
                    aria-label="Collapse row"
                    onClick={(e) => onExpandByIcon(record, !expanded, e)}
                ></button>
            ) : (
                <button
                    type="button"
                    className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
                    aria-label="Expand row"
                    onClick={(e) => onExpandByIcon(record, !expanded, e)}
                ></button>
            ))
    }
}

export const onExpandOnlyOneRow = (setExpandedRowKeys: Function) => {
    // This method is used to generate the following method: Table.expandable.onExpand
    // To avoid expanding multiple rows to be open in one table at the same time.
    // This is crucial in tables with LazyExpandedRow component
    const onExpand = (expanded: boolean, record: any) => {
        if (expanded)
            setExpandedRowKeys([record.id])
        else
            setExpandedRowKeys([])
    }
    return onExpand
}


export const LiveTableWrapper = (
    { children, refetch }: { children: React.ReactNode; refetch: Function}
) => {
    const generalSettings = useGetGeneralSettings();
    const GUITableSettings = generalSettings.find(setting => setting.name == 'GUITableSettings');

    useEffect(() => {
        if(GUITableSettings?.config?.ENABLED == true) {
            const refreshInterval = GUITableSettings?.config?.table_refresh_interval;
            if(refreshInterval){
                const interval = setInterval(() => {refetch();}, refreshInterval*1000);
                return () => clearInterval(interval);
            }
        }
    }, [refetch]);
    return <>{children}</>
}

/**
 * Custom hook for managing expandable rows with URL hash synchronization
 *
 * This hook manages the state of expandable rows in a table and keeps the URL hash
 * in sync with the currently expanded row. It allows only one row to be expanded at a time.
 *
 * @param rowKeyField - The field name to use as the row key (defaults to 'name')
 * @returns An object containing:
 *   - expandedRowKeys: Array of currently expanded row keys
 *   - onExpand: Handler for the Table's expandable.onExpand prop
 *   - toggleRowExpansion: Handler for toggling row expansion via the expand icon
 *
 * @example
 * const { expandedRowKeys, onExpand, toggleRowExpansion } = useExpandableRows('name');
 *
 * <Table
 *   expandable={{
 *     onExpand,
 *     expandedRowKeys,
 *     expandIcon: onExpandIcon(toggleRowExpansion, onExpand)
 *   }}
 * />
 */
export const useExpandableRows = (rowKeyField: string = 'name') => {
    const location = useLocation();
    const history = useHistory();
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

    // Sync expandedRowKeys with URL hash
    useEffect(() => {
        const hashKey = location.hash.substring(1);
        if (hashKey) {
            setExpandedRowKeys([hashKey]);
        } else {
            setExpandedRowKeys([]);
        }
    }, [location.hash]);

    // Handler for table row expansion
    const onExpand = (expanded: boolean, record: any) => {
        const key = record[rowKeyField];
        if (expanded) {
            setExpandedRowKeys([key]);
            history.push(`${location.pathname}${location.search}#${key}`);
        } else {
            setExpandedRowKeys([]);
            history.push(`${location.pathname}${location.search}`);
        }
    };

    // Handler for toggling row expansion via expand icon
    const toggleRowExpansion = (key: any) => {
        if (expandedRowKeys.includes(key)) {
            setExpandedRowKeys([]);
            history.push(`${location.pathname}${location.search}`);
        } else {
            setExpandedRowKeys([key]);
            history.push(`${location.pathname}${location.search}#${key}`);
        }
    };

    return {
        expandedRowKeys,
        onExpand,
        toggleRowExpansion
    };
};
